/**
 * @author Ahmed Samer
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2020-08-23
 * @description .
 * @filename .tsx
 */

import React from "react";
import { GRAPHQL_TYPE_PERSON, Person } from "@radivision/graphql/lib/ts/graphql/person";
import { PreviewKind } from "@radivision/graphql/lib/ts/graphql/preview-type";
import { TopLevelMediaKind } from "@radivision/graphql/lib/ts/graphql/top-level-media-type";
import { SVG } from "../../component-configuration/svgs";
import { ApiClient } from "../../authentication/api-client";
import { ArraysHelper } from "../../utilities/arrays";
import { ImageHelper } from "../../utilities/image-helper";
import { REACT_USER_CONTEXT } from "../../contexts/react-user-context";
import { MqttClient } from "../../utilities/mqtt";
import { PanelNavbar } from "./panel-navbar";
import isEqual from "lodash.isequal";

/**
 *
 *
 */
const dummyImageSrc = ImageHelper.fetchOptimizedImageUrl({
  imageType: "DEFAULT",
  default: {
    previewType: PreviewKind.SQUARE,
    mediaType: TopLevelMediaKind.IMAGE,
    type: GRAPHQL_TYPE_PERSON,
  },
  desiredDimensions: { containerWidthRatio: 1, numberOfItems: 6 },
  isSquareImage: true,
  revision: undefined,
});

const DUMMY_IMAGE = dummyImageSrc ? dummyImageSrc.requestedResolutionUrl : null;
/**
 *
 *
 * @interface CommentEntity
 * @exports
 */
export interface CommentEntity {
  meta?: {
    /**
     * commenter name
     *
     * @type {string}
     * @memberof CommentEntity
     */
    authorName?: string;

    /**
     *
     *
     * @type {string}
     */
    authorId?: string;

    /**
     *
     *
     * @type {string}
     * @memberof CommentEntity
     */
    avatarUrl?: string;
  };
  /**
   *
   *
   * @type {string}
   * @memberof CommentEntity
   */
  id: string;

  /**
   *
   *
   * @type {string}
   * @memberof CommentEntity
   */
  text: string;

  /**
   *
   *
   * @type {string}
   * @memberof CommentEntity
   */
  created: number;

  isDeleted?: boolean;
}

/**
 *
 *
 * @export
 * @interface CommentAreaProps
 */
export interface CommentAreaProps {
  /**
   *
   *
   * @type {boolean}
   * @memberof CommentAreaProps
   */
  isAuthenticated: boolean;

  /**
   * to dismiss the chat box
   *
   * @type {() => void}
   * @memberof CommentAreaProps
   */
  onDismiss: () => void;

  /**
   *
   *
   * @type {string}
   * @memberof CommentAreaProps
   */
  eventId: string;

  /**
   * toggle the live chat box visiblity
   *
   * @type {() => void}
   * @memberof CommentAreaProps
   */
  toggleVisibility: () => void;

  /**
   * flag of which to know live chat box visible or not
   *
   * @type {boolean}
   * @memberof CommentAreaProps
   */
  visible: boolean;

  /**
   * livestreaming youtube video id
   *
   * @type {string}
   * @memberof CommentAreaProps
   */
  youtube: string;
}

/**
 * interface that defines the state of the component
 *
 * @interface
 */
interface CommentAreaState {
  /**
   * the loading state of the comment button
   *
   * @type {boolean}
   * @memberof CommentAreaState
   */
  loading: boolean;

  /**
   * Event comments on live chat when event is live
   *
   * @type {CommentEntity[]}
   * @memberof VideoPanelState
   */
  comments: CommentEntity[];

  /**
   * when user/guest published a comment, display info message that will tell him the comment is underReview
   *
   * @type {boolean}
   * @memberof VideoPanelState
   */
  commentUnderReview: boolean;

  /**
   * the number of seen comments, the length of comments array when the chatbox is opened
   *
   * @type {number}
   * @memberof VideoPanelState
   */
  seenComments: number;

  /**
   *
   *
   * @type {string}
   * @memberof CommentAreaState
   */
  nextPageToken?: string;

  /**
   *
   *
   * @type {string}
   * @memberof CommentAreaState
   */
  startKey?: string;
}

export class CommentArea extends React.Component<CommentAreaProps, CommentAreaState> {
  textAreaRef: any;
  static contextType: React.Context<{
    user: Person<string>;
    updateUser: (user: Person<string>) => any;
  }> = REACT_USER_CONTEXT;

  constructor(props: CommentAreaProps) {
    super(props);

    this.state = {
      loading: false,
      commentUnderReview: false,
      comments: [],
      seenComments: 0,
    };

    this.textAreaRef = React.createRef();
    this.publishComment = this.publishComment.bind(this);
    this.updateCommentView = this.updateCommentView.bind(this);
  }

  /**
   *
   *
   * @memberof CommentArea
   */
  componentDidMount() {
    this.updateCommentView();
    setInterval(this.updateCommentView, 10000);
  }

  /**
   *
   *
   * @param {CommentAreaProps} prevProps
   * @memberof CommentArea
   */
  componentDidUpdate(prevProps: CommentAreaProps, prevState: CommentAreaState) {
    if (
      !isEqual(this.state.comments, prevState.comments) ||
      this.state.commentUnderReview !== prevState.commentUnderReview ||
      (this.props.visible !== prevProps.visible && this.props.visible === true)
    ) {
      this.scrollToFreshComments();
    }
  }

  /**
   *
   *
   * @memberof VideoPanel
   */
  updateCommentView = (): Promise<any> => {
    const URL = `${process.env.LIVE_COMMENTS_ENDPOINT}?recordId=${this.props.eventId || this.props.youtube}${
      this.state.startKey ? `&exclusiveStartKey=${this.state.startKey}` : ""
    }${this.state.nextPageToken ? `&pageToken=${this.state.nextPageToken}` : ""}`;
    return ApiClient.get(URL).then((res) => {
      if (res) {
        const COMMENTS: CommentEntity[] = /*ArraysHelper.unionBy([...this.state.comments], res.comments, "id")*/ res.comments.filter(
          (comment: CommentEntity) => {
            return !comment.isDeleted;
          },
        ) as CommentEntity[];

        if (this.props.visible) {
          this.setState({ seenComments: COMMENTS.length });
        }
        this.setState({
          comments: COMMENTS,
          startKey: res.exclusiveStartKey,
          nextPageToken: res.nextPageToken,
        });
      }
    });
  };

  /**
   *
   *
   * @param {string} text
   * @memberof CommentArea
   */
  publishComment(text: string) {
    if (this.state.loading) return;

    this.setState({ loading: true });

    const { user } = this.context;
    const META: {
      /**
       * commenter name
       *
       * @type {string}
       * @memberof CommentEntity
       */
      authorName?: string;

      /**
       *
       *
       * @type {string}
       */
      authorId?: string;

      /**
       *
       *
       * @type {string}
       * @memberof CommentEntity
       */
      avatarUrl?: string;
    } = {};

    if (user) {
      const AVATAR_URL: string = user.previews
        ? ImageHelper.fetchOptimizedImageUrl({
            imageType: "PREVIEW",
            preview: {
              content: user.previews,
              previewKind: PreviewKind.HEADSHOT,
              topLevelMedia: TopLevelMediaKind.IMAGE,
              isDefaultPreview: true,
              type: GRAPHQL_TYPE_PERSON,
            },
            isSquareImage: true,
            desiredDimensions: {
              containerWidthRatio: 3 / 12,
              numberOfItems: 4,
            },
            revision: user.revision,
          }).screenResolutionUrl
        : DUMMY_IMAGE;

      META.authorId = user.id;
      META.authorName = user.fullName;
      META.avatarUrl = AVATAR_URL;
    }

    MqttClient.publishMessageOnTopic(text, "Mqtt", this.props.eventId || this.props.youtube, META)
      .then((res) => {
        return Promise.resolve();
      })
      .catch((err) => {
        console.error(err);
      })
      .then(() => {
        this.textAreaRef.current.value = "";
        this.setState({ loading: false });
        setTimeout(() => {
          this.setState({ commentUnderReview: false });
        }, 3000);
      })
      .catch((err) => {
        console.error(err);
      });
  }

  /**
   * to automatically scroll to the latest comments
   */
  scrollToFreshComments() {
    // const scrollIntoViewOptions: ScrollIntoViewOptions = {
    //   behavior: "smooth",
    //   block: "end",
    // };
    // const elem = document.getElementById("comments-block");
    // if (elem && "scrollIntoView" in elem) elem.scrollIntoView(scrollIntoViewOptions);
  }

  /**
   * calculate elapsed time since the comment has been posted
   *
   * @param {Date} date
   * @returns {string} period elasped as text
   * @memberof CommentArea
   */
  timeSince(date: Date): string {
    const SECONDS = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    let interval: number = SECONDS / 31536000;

    if (interval > 1) {
      return `${Math.floor(interval)} years`;
    }
    interval = SECONDS / 2592000;
    if (interval > 1) {
      return `${Math.floor(interval)} months`;
    }
    interval = SECONDS / 86400;
    if (interval > 1) {
      return `${Math.floor(interval)} days`;
    }
    interval = SECONDS / 3600;
    if (interval > 1) {
      return `${Math.floor(interval)} hours`;
    }
    interval = SECONDS / 60;
    if (interval > 1) {
      return `${Math.floor(interval)} minutes`;
    }
    return `${Math.floor(SECONDS)} seconds`;
  }

  render() {
    const { user } = this.context;

    const CommenterAvatar: string =
      user && user.previews
        ? ImageHelper.fetchOptimizedImageUrl({
            imageType: "PREVIEW",
            preview: {
              content: user.previews,
              previewKind: PreviewKind.HEADSHOT,
              topLevelMedia: TopLevelMediaKind.IMAGE,
              isDefaultPreview: true,
              type: GRAPHQL_TYPE_PERSON,
            },
            isSquareImage: true,
            desiredDimensions: {
              containerWidthRatio: 3 / 12,
              numberOfItems: 4,
            },
            revision: user.revision,
          }).screenResolutionUrl
        : DUMMY_IMAGE;

    return this.props.visible ? (
      <div className="comments-container">
        <div className="comment-box-head">
          <div className="cmtbx-head">
            <div className="cmnt-head-bk" onClick={this.props.onDismiss}>
              {SVG.chevronLeft}
              <span>Live Chat</span>
            </div>
            <i onClick={this.props.onDismiss}>{SVG.crosshairs}</i>
          </div>
        </div>
        <div className="comments-wrap">
          <div
            className={`comments${!this.state.comments.length && !this.state.commentUnderReview ? " flex-box" : ""}`}
            id="comments-block"
          >
            {this.state.comments.length ? (
              this.state.comments.map((comment, index) => {
                return (
                  <div className="comment-wrap" key={index}>
                    <div className="comment-block">
                      <div className="photo">
                        <div
                          className="avatar"
                          style={{
                            backgroundImage: `url('${
                              comment.meta && comment.meta.avatarUrl ? comment.meta.avatarUrl : DUMMY_IMAGE
                            }')`,
                          }}
                        />
                        <div className="displayname">
                          {comment.meta && comment.meta.authorName ? comment.meta.authorName : "Anonymous"}
                        </div>
                        <div className="comment-date">{`${this.timeSince(new Date(comment.created))} ago`}</div>
                      </div>
                      <p className="comment-text">{comment.text}</p>
                    </div>
                  </div>
                );
              })
            ) : !this.state.commentUnderReview ? (
              <div className="empty-inbox">
                <p>No Comments yet!!</p>
                <p>be the first to comment.</p>
              </div>
            ) : null}
            {this.state.commentUnderReview ? (
              <div className="comment-wrap info" key={this.state.comments.length}>
                <div className="comment-block">
                  <p className="comment-text">
                    Your comment is under review, it will appear here as soon as it gets approved.
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="new-comment">
          <div className="photo">
            <div
              className="avatar"
              style={{
                backgroundImage: `url('${CommenterAvatar}')`,
              }}
            />
          </div>
          <form action="">
            <div className="comment-block">
              <textarea
                disabled={this.state.loading}
                ref={this.textAreaRef}
                name=""
                id="aa"
                rows={2}
                placeholder="Add comment..."
              />
            </div>
            <button
              className="btn pmd-btn-fab pmd-ripple-effect"
              type="button"
              disabled={this.state.loading}
              onClick={() => {
                this.publishComment(this.textAreaRef.current.value);
              }}
            >
              {SVG.send}
            </button>
          </form>
        </div>
      </div>
    ) : (
      <div className="open-chatbox" onClick={this.props.toggleVisibility}>
        <div className="icon">
          {this.state.seenComments < this.state.comments.length ? (
            <div className="badge">{this.state.comments.length - this.state.seenComments}</div>
          ) : null}
          {SVG.chat}
        </div>
        <span>Live Chat</span>
      </div>
    );
  }
}
